import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { createScanApi } from "../../scripts/api_scan";
import { createScanWorkflowApi } from "../../scripts/api_scan_workflow";
import ScanLoading from "../../components/Scan/ScanLoading";
import ScanReport from "./ScanReport";
import ScanWorkflow from "./ScanWorkflow";
import ScanForm from "./ScanForm";
import ScanProgressPreview from "./ScanProgressPreview";
import { getScanPaymentReturn } from "./scanPaymentReturn";
import Table from "../../components/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShield } from "@fortawesome/free-solid-svg-icons";
import { ArrowBackRounded } from "@mui/icons-material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const scanKey = scan =>
    JSON.stringify([
        scan.scan_id,
        scan.date_folder,
    ]);

const scanStatusLabels = {
    awaiting_signature: "En attente de signature",
    signed: "Signé",
    awaiting_payment: "En attente de règlement",
    paid: "En cours",
    pending_creation: "En Cours",
    launching: "En Cours",
    pending: "En Cours",
    queued: "En Cours",
    running: "En Cours",
    processing: "En Cours",
    in_progress: "En Cours",
    completed: "Terminé",
    failed: "Erreur",
    launch_failed: "Échec du lancement",
    launch_unknown: "Lancement à vérifier",
    blocked: "Vérification nécessaire",
    unknown: "État inconnu",
};

const isScanInProgress = scan => [
    "pending_creation", "launching", "pending", "queued", "running", "processing", "in_progress",
].includes(scan.scan_status ?? scan.status)
    || ((scan.scan_status ?? scan.status) === "completed" && scan.report_available === false);

function ScanScoreGauge({ score }) {
    const numericScore = Number(score);
    const hasScore = score !== null && score !== undefined && score !== "" && Number.isFinite(numericScore);
    const value = hasScore
        ? Math.max(0, Math.min(100, numericScore))
        : 0;
    const circumference = 2 * Math.PI * 13;
    const strokeOffset = circumference * (1 - value / 100);
    const tone = !hasScore
        ? "unknown"
        : value >= 80
            ? "high"
            : value >= 50
                ? "medium"
                : "low";

    return (
        <span
            className={`scan-score-gauge scan-score-gauge--${tone}`}
            aria-label={hasScore ? `Score ${score} sur 100` : "Score indisponible"}
        >
            <svg
                className="scan-score-gauge-ring"
                viewBox="0 0 32 32"
                role="img"
                aria-hidden="true"
            >
                <circle
                    className="scan-score-gauge-ring-track"
                    cx="16"
                    cy="16"
                    r="13"
                />
                <circle
                    className="scan-score-gauge-ring-value"
                    cx="16"
                    cy="16"
                    r="13"
                    style={{
                        strokeDasharray: circumference,
                        strokeDashoffset: strokeOffset,
                    }}
                />
            </svg>
            <strong>{hasScore ? score : "—"}</strong>
        </span>
    );
}

export default function Scan() {
    const { t } = useTranslation();

    const {
        apiUrl,
        user,
        token,
    } = useContext(AuthContext);

    const scanApi = useMemo(
        () => createScanApi(apiUrl),
        [apiUrl]
    );

    const workflowApi = useMemo(
        () => createScanWorkflowApi(apiUrl, token),
        [apiUrl, token]
    );

    const [searchParams] = useSearchParams();

    const returnState = getScanPaymentReturn(
        searchParams
    );

    const hasCheckoutReturn =
        returnState.kind !== "none";

    const [view, setView] = useState(
        () => hasCheckoutReturn
            ? "tracking"
            : "list"
    );

    const [list, setList] = useState({
        loading: true,
        scans: [],
        entreprise: null,
        error: null,
    });

    const [selected, setSelected] = useState(null);
    const [previewRequest, setPreviewRequest] = useState(null);
    const [paymentAccepted, setPaymentAccepted] = useState(false);
    const [trackingNotice, setTrackingNotice] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const reducedMotion = useReducedMotion();

    /*
     * Un retour Stripe/DocuSign doit ouvrir
     * directement le workflow, jamais la liste.
     */
    useEffect(() => {
        if (hasCheckoutReturn) {
            setView("tracking");
        }
    }, [hasCheckoutReturn]);

    /*
     * La liste des scans n'est chargée QUE
     * lorsque la vue liste est affichée.
     */
    useEffect(() => {
        if (view !== "list") {
            return;
        }

        const controller = new AbortController();

        setList(previous => ({
            ...previous,
            loading: true,
            error: null,
        }));

        scanApi
            .list(controller.signal)
            .then(data => {
                if (controller.signal.aborted) {
                    return;
                }

                setList({
                    loading: false,
                    scans: Array.isArray(data?.scans) ? data.scans : [],
                    entreprise: data?.entreprise ?? null,
                    error: null,
                });
            })
            .catch(error => {
                if (controller.signal.aborted) {
                    return;
                }

                const status = error?.response?.status ?? error?.status;
                const isEmptyResponse = [204, 404].includes(status);

                setList({
                    loading: false,
                    scans: [],
                    entreprise: null,
                    error: isEmptyResponse ? null : error,
                });
            });

        return () => controller.abort();

    }, [
        scanApi,
        user,
        refresh,
        view,
    ]);

    const openProgress = request => {
        setPreviewRequest(request);
        setView("preview");
    };

    const openProgressAfterPayment = request => {
        setPaymentAccepted(true);
        openProgress(request);
    };

    const dismissPaymentNotice = useCallback(() => {
        setPaymentAccepted(false);
    }, []);

    const handleRequestCreated = useCallback(request => {
        setTrackingNotice({
            type: "success",
            title: "Demande envoyée",
            message: "Votre demande a été envoyée pour signature. Vous pouvez suivre son avancement ci-dessous.",
            requestId: request?.request_id ?? null,
        });
        setView("tracking");
    }, []);

    const handleRequestCreationError = useCallback(error => {
        const isAuthenticationError =
            error?.code === "authentication"
            || [401, 403].includes(error?.response?.status);

        setTrackingNotice({
            type: "warning",
            title: isAuthenticationError
                ? "Vérification nécessaire"
                : "Envoi de la signature à vérifier",
            message: isAuthenticationError
                ? "Reconnectez-vous si nécessaire, puis vérifiez la demande dans le suivi avant de renvoyer la signature."
                : "La demande est disponible dans le suivi, mais l’envoi pour signature n’a pas pu être confirmé. Renvoyez la demande de signature si nécessaire.",
            requestId: null,
        });
        setView("tracking");
    }, []);

    useEffect(() => {
        if (!trackingNotice) {
            return undefined;
        }

        const timeout = window.setTimeout(() => {
            setTrackingNotice(null);
        }, 6000);

        return () => window.clearTimeout(timeout);
    }, [trackingNotice]);

    const openReport = request => {
        setPaymentAccepted(false);
        setPreviewRequest(null);
        setSelected({
            ...request,
            scan_id: request.scan_id,
            date_folder:
                request.report_date_folder
                ?? request.date_folder
                ?? null,
            report_id: request.report_id ?? null,
            report_url: request.report_url ?? null,
            date_fr: request.completed_at
                || request.updated_at
                || request.date_fr
                || null,
        });
        setView("detail");
    };

    return (
        <div className="ecp-container scan-page">
                <div className="scan-workflow-toast-position">
                    <AnimatePresence>
                        {view === "tracking" && trackingNotice && (
                            <motion.div
                                key={`${trackingNotice.type}:${trackingNotice.requestId ?? "request"}`}
                                className={`scan-payment-accepted-toast${trackingNotice.type === "warning" ? " scan-payment-accepted-toast--warning" : ""}`}
                                role={trackingNotice.type === "warning" ? "alert" : "status"}
                                aria-live="polite"
                                aria-atomic="true"
                                initial={{
                                    opacity: 0,
                                    y: reducedMotion ? 0 : -60,
                                }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{
                                    opacity: 0,
                                    y: reducedMotion ? 0 : -24,
                                }}
                                transition={{
                                    duration: reducedMotion ? 0 : 0.3,
                                    ease: "easeOut",
                                }}
                            >
                                <span
                                    className="scan-payment-accepted-toast-icon"
                                    aria-hidden="true"
                                >
                                    {trackingNotice.type === "success" ? "✓" : "!"}
                                </span>
                                <span>
                                    <strong>{t(trackingNotice.title)}</strong>
                                    <small>{t(trackingNotice.message)}</small>
                                </span>
                                <button
                                    type="button"
                                    aria-label={t("Fermer")}
                                    onClick={() => setTrackingNotice(null)}
                                >
                                    ×
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

            <div className="ecp-container-content">

                <div className="ecp-container-content-header">
                    <div className="page-title page-logo">
                        <div className="card-background-logo card-logo-firewall">
                            <FontAwesomeIcon
                                icon={faShield}
                                color="#FA8334"
                            />
                        </div>

                        <h1 className="page-title">
                            {t(
                                "Dynamic Application Security Testing"
                            )}
                        </h1>
                    </div>

                    {view === "list" && (
                        <div className="scan-main-actions">
                            <button
                                type="button"
                                className="buttons-ecp buttons-ecp-create"
                                onClick={() =>
                                    setView("tracking")
                                }
                            >
                                {t("Suivi")}
                            </button>

                            <button
                                type="button"
                                className="buttons-ecp buttons-ecp-new"
                                onClick={() =>
                                    setView("form")
                                }
                            >
                                {t("Nouvelle demande")}
                            </button>
                        </div>
                    )}
                </div>

                <div className="scan-page-content">

                    {view === "list" && (
                        <ScanHistory
                            list={list}
                            onOpenProgress={openProgress}
                            onOpenTracking={() => setView("tracking")}
                            onSelect={scan => {
                                setSelected(scan);
                                setView("detail");
                            }}
                            onRetry={() =>
                                setRefresh(value => value + 1)
                            }
                        />
                    )}

                    {view === "form" && (
                        <section>
                            <div className="scan-history-header">
                                <button
                                    type="button"
                                    className="scan-workflow-back-link"
                                    onClick={() =>
                                        setView("list")
                                    }
                                >
                                    <ArrowBackRounded aria-hidden="true" />
                                    {t("Retour aux scans")}
                                </button>
                            </div>

                            <ScanForm
                                api={workflowApi}
                                onCreated={handleRequestCreated}
                                onViewRequests={handleRequestCreationError}
                            />
                        </section>
                    )}

                    {view === "tracking" && (
                        <ScanWorkflow
                            returnOnly={hasCheckoutReturn}
                            onBack={() => {
                                setTrackingNotice(null);
                                setView("list");
                            }}
                            onOpenProgress={openProgress}
                            onPaymentConfirmed={openProgressAfterPayment}
                        />
                    )}

                    {view === "preview" && previewRequest && (
                        <ScanProgressPreview
                            api={workflowApi}
                            request={previewRequest}
                            paymentAccepted={paymentAccepted}
                            onPaymentNoticeShown={dismissPaymentNotice}
                            onBack={() => {
                                dismissPaymentNotice();
                                setView("tracking");
                            }}
                            onViewScan={openReport}
                        />
                    )}

                    {view === "detail" && selected && (
                        <ScanDetail
                            key={scanKey(selected)}
                            api={scanApi}
                            selected={selected}
                            onBack={() => {
                                setSelected(null);
                                setView("list");
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function ScanHistory({
    list,
    onSelect,
    onRetry,
    onOpenProgress,
    onOpenTracking
}) {
    const { t } = useTranslation();

    const tableData = useMemo(
        () => list.scans.map(scan => {
            const statusKey = scan.scan_status ?? scan.status;

            return {
                id: scanKey(scan),
                date: scan.date_fr || scan.date_folder || "—",
                target:
                    scan.target_url
                    ?? scan.target
                    ?? scan.url
                    ?? "—",
                status: scanStatusLabels[statusKey]
                    || statusKey
                    || "—",
                statusTone:
                    ["completed", "paid"].includes(statusKey)
                        ? "success"
                        : isScanInProgress(scan) || [
                            "awaiting_signature",
                            "awaiting_payment",
                        ].includes(statusKey)
                            ? "pending"
                            : "unknown",
                score: isScanInProgress(scan) ? "—" : scan.score ?? "—",
                scan,
            };
        }),
        [list.scans]
    );

    const tableColumns = useMemo(() => [
        {
            Header: "Date",
            accessor: "date",
            headerColumn: true,
            filterable: true,
        },
        {
            Header: "Cible",
            accessor: "target",
            headerColumn: true,
            filterable: true,
        },
        {
            Header: "Statut",
            accessor: "status",
            headerColumn: true,
            filterable: true,
            Cell: ({ cell }) => (
                <span className={`scan-status-badge scan-status-badge--${cell.row.original.statusTone}`}>
                    {cell.value}
                </span>
            ),
        },
        {
            Header: "Score",
            accessor: "score",
            headerColumn: true,
            filterable: true,
            Cell: ({ cell }) => {
                return (
                    <ScanScoreGauge score={cell.value} />
                );
            },
        },
        {
            Header: "Actions",
            accessor: "scan_id",
            headerColumn: true,
            filterable: false,
            Cell: ({ cell }) => {
                const scan = cell.row.original.scan;
                const statusKey = scan.scan_status ?? scan.status;

                if (["awaiting_signature", "awaiting_payment"].includes(statusKey)) {
                    return (
                        <button
                            type="button"
                            className="buttons-ecp scan-view-button"
                            onClick={onOpenTracking}
                        >
                            {t("Voir le suivi")}
                        </button>
                    );
                }

                const inProgress = isScanInProgress(scan) || statusKey === "paid";
                if (inProgress) {
                    return (
                        <button
                            type="button"
                            className="buttons-ecp scan-view-button"
                            disabled={!scan.request_id && !scan.uuid}
                            title={(!scan.request_id && !scan.uuid) ? t("Le suivi de ce scan n’est pas encore associé à une demande.") : undefined}
                            onClick={() => onOpenProgress({ ...scan, request_id: scan.request_id ?? scan.uuid })}
                        >
                            {t("Voir la progression")}
                        </button>
                    );
                }

                return (
                    <button
                        type="button"
                        className="buttons-ecp scan-view-button"
                        onClick={() => onSelect(scan)}
                    >
                        {t("VOIR")}
                    </button>
                );
            },
        },
    ], [onSelect, onOpenProgress, t]);

    return (
        <section
            className="scan-history"
            aria-label={t("Scans disponibles")}
        >
            {list.loading && <ScanLoading variant="table" />}

            {!list.loading && (
                <Table
                    tableData={tableData}
                    tableColumns={tableColumns}
                    FileSaverExcelName="scans"
                />
            )}
        </section>
    );
}

function ScanDetail({ api, selected, onBack }) {
    const { t } = useTranslation();

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retry, setRetry] = useState(0);

    const [fileLoading, setFileLoading] = useState(false);
    const [fileError, setFileError] = useState(null);

    const fileController = useRef(null);

    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError(null);
        setDetail(null);

        api.detail(selected, controller.signal)
            .then(data => {
                if (!controller.signal.aborted) {
                    setDetail(data);
                }
            })
            .catch(error => {
                if (!controller.signal.aborted) {
                    setError(error);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();

    }, [api, selected, retry]);

    useEffect(
        () => () => fileController.current?.abort(),
        []
    );

    const pdfFile =
        detail?.pdf_file
        ?? selected.pdf_file;

    const hasPdf = Boolean(
        pdfFile?.view_url
        || pdfFile?.filename
    );

    const report = detail?.json;

    const hasReport =
        report
        && typeof report === "object"
        && !Array.isArray(report)
        && (
            report.summary?.counts
            || Array.isArray(report.findings)
        );

    const downloadReport = async () => {
        if (!hasPdf || fileController.current) {
            return;
        }

        const controller = new AbortController();

        fileController.current = controller;

        setFileLoading(true);
        setFileError(null);

        try {
            const {
                blob,
                filename,
            } = await api.file(
                pdfFile,
                selected.date_folder,
                {
                    download: true,
                    signal: controller.signal,
                }
            );

            if (controller.signal.aborted) {
                return;
            }

            if (
                !(await blob.slice(0, 5).text())
                    .startsWith("%PDF-")
            ) {
                throw new Error("Invalid PDF");
            }

            if (controller.signal.aborted) {
                return;
            }

            const url = URL.createObjectURL(
                new Blob(
                    [blob],
                    {
                        type: "application/pdf",
                    }
                )
            );

            const link =
                document.createElement("a");

            link.href = url;
            link.download = filename;
            link.hidden = true;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.setTimeout(
                () => URL.revokeObjectURL(url),
                60_000
            );

        } catch {
            if (!controller.signal.aborted) {
                setFileError(
                    t(
                        "Impossible de charger le PDF. Veuillez réessayer."
                    )
                );
            }

        } finally {
            if (!controller.signal.aborted) {
                setFileLoading(false);
            }

            if (
                fileController.current === controller
            ) {
                fileController.current = null;
            }
        }
    };

    return (
        <section
            className="scan-detail"
            aria-label={t("Résultats du scan")}
            aria-busy={loading}
        >
            <div className="scan-history-header">
                <button
                    type="button"
                    className="buttons-ecp buttons-ecp-create"
                    onClick={onBack}
                >
                    {t("Retour")}
                </button>

                <h2>
                    {t("Scan du")}{" "}
                    {selected.date_fr
                        || selected.date_folder}
                </h2>
            </div>

            {loading && (
                <ScanLoading variant="report" />
            )}

            {error && (
                <div
                    role="alert"
                    className="scan-error"
                >
                    <p>
                        {t(
                            "Impossible de charger ce scan."
                        )}
                    </p>

                    <button
                        type="button"
                        className="buttons-ecp buttons-ecp-create"
                        onClick={() =>
                            setRetry(value => value + 1)
                        }
                    >
                        {t("Réessayer")}
                    </button>
                </div>
            )}

            {fileError && (
                <p
                    role="alert"
                    className="scan-error"
                >
                    {fileError}
                </p>
            )}

            {!loading && !error && !hasPdf && (
                <p>
                    {t(
                        "Aucun PDF disponible pour ce scan."
                    )}
                </p>
            )}

            {!loading
                && !error
                && (
                    hasReport
                        ? (
                            <ScanReport
                                report={report}
                                onViewReport={
                                    hasPdf
                                        ? downloadReport
                                        : undefined
                                }
                                reportLoading={
                                    fileLoading
                                }
                            />
                        )
                        : (
                            <p role="status">
                                {t(
                                    "Les données du rapport sont indisponibles ou dans un format non pris en charge."
                                )}
                            </p>
                        )
                )}
        </section>
    );
}
